import type { ReactNode } from "react";
import { Link as ChakraLink } from "@chakra-ui/react";

/**
 * A link inside running text: a name in a sentence that opens its page.
 *
 * Visible at rest, because a name in a sentence otherwise reads as text: the
 * underline is always there, in a light blue that marks it as a link without
 * turning every line it sits in blue. Hover and keyboard focus then make it
 * unmistakable -- the text takes the link colour and the underline darkens to
 * match -- so pointing at it gives an answer, which a permanent underline alone
 * did not.
 *
 * Hover-only underline stays right for a table cell that is nothing BUT a name;
 * this is for the other case. Chakra's own `variant="underline"` draws the line
 * at 20% of the text colour, which is the faintness this exists to avoid.
 *
 * Wraps the router link it is given (`asChild`), so navigation stays the
 * router's and only the look is decided here.
 */
export function TextLink({ children }: { children: ReactNode }) {
  return (
    <ChakraLink
      asChild
      fontWeight="medium"
      color="fg"
      textDecoration="underline"
      textDecorationColor="blue.muted"
      textDecorationThickness="1px"
      textUnderlineOffset="3px"
      transition="color 0.15s, text-decoration-color 0.15s"
      _hover={{ color: "blue.fg", textDecorationColor: "blue.fg" }}
      _focusVisible={{ color: "blue.fg", textDecorationColor: "blue.fg" }}
    >
      {children}
    </ChakraLink>
  );
}
